/**
 * Customer-selected reasons when cancelling a subscription.
 * Stored on cancel audit / scheduled_changes.payload.
 */
export const CANCELLATION_REASONS = [
  "too_expensive",
  "change_plan",
  "not_using",
  "no_results",
  "bad_content",
  "hard_to_use",
  "missing_features",
  "technical_issues",
  "switching_tool",
  "business_closed",
  "other",
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

/**
 * Inside the 7-day window the confirm screen offers two ways out: stop the renewal and keep access
 * until the period ends, or cancel now and get the money back. A request without a choice comes
 * from a page opened before the choice existed and keeps the old rule (refund when eligible).
 */
export const CANCELLATION_CHOICES = ["stop_renewal", "refund_now"] as const;
export type CancellationChoice = (typeof CANCELLATION_CHOICES)[number];

/**
 * What `cancellation_choice` holds in the audit detail of `cancellation.scheduled` and
 * `cancellation.refunded`: the choice, or a refund asked for after stopping the renewal.
 */
export type RecordedCancellationChoice = CancellationChoice | "refund_after_stop_renewal";

/**
 * What the customer can tick under the reason they picked (any number, none required). Built
 * from support's cancellation taxonomy ("Categoria de chamados" → "Como registrar cancelamentos")
 * mapped onto the reason codes zenpay already stores, so the reason history stays comparable.
 * Reasons with nothing here go straight to the explanation.
 */
export const CANCELLATION_SUBREASONS = {
  too_expensive: ["over_budget", "cutting_costs"],
  change_plan: [],
  not_using: ["no_time", "just_testing", "forgot"],
  no_results: ["low_engagement", "no_new_clients", "posted_too_little"],
  bad_content: ["bad_images", "bad_texts", "not_creative", "wrong_niche"],
  hard_to_use: [
    "connect_instagram",
    "create_posts",
    "edit_posts",
    "schedule_publish",
    "understand_app",
    "login",
  ],
  missing_features: [
    "text_editing",
    "image_editing",
    "upload_image",
    "video",
    "audio",
    "auto_logo",
    "auto_publish",
    "other_networks",
  ],
  technical_issues: ["instagram_connection", "posts_not_published", "generation_error", "login", "slow"],
  switching_tool: ["other_app", "hired_someone", "do_it_myself"],
  business_closed: [],
  other: [],
} as const satisfies Record<CancellationReason, readonly string[]>;

export function subreasonsFor(reason: CancellationReason): readonly string[] {
  return CANCELLATION_SUBREASONS[reason];
}

/** Valid subreasons of `reason`, once each, in list order, so one set always stores the same way. */
export function normalizeSubreasons(reason: CancellationReason, subreasons: readonly string[]): string[] {
  return subreasonsFor(reason).filter((s) => subreasons.includes(s));
}

/** Null when every subreason belongs to `reason`; otherwise the first one that does not. */
export function invalidSubreason(reason: CancellationReason, subreasons: readonly string[]): string | null {
  const allowed = subreasonsFor(reason);
  return subreasons.find((s) => !allowed.includes(s)) ?? null;
}

/**
 * The help offered once the reason is known. Only reasons a person can fix get an offer:
 * hard to use or broken → talk to support now; weak results or content → a session with the
 * team. Everyone else goes straight on, and every offer screen keeps "continue cancelling"
 * in plain view — the 7-day right of withdrawal (CDC art. 49) lets us help, not obstruct.
 */
export type CancelRescueKind = "whatsapp_now" | "specialist" | "none";

const RESCUE_BY_REASON: Record<CancellationReason, CancelRescueKind> = {
  too_expensive: "none",
  change_plan: "none",
  not_using: "none",
  no_results: "specialist",
  bad_content: "specialist",
  hard_to_use: "whatsapp_now",
  missing_features: "none",
  technical_issues: "whatsapp_now",
  switching_tool: "none",
  business_closed: "none",
  other: "none",
};

export function rescueForReason(reason: CancellationReason): CancelRescueKind {
  return RESCUE_BY_REASON[reason];
}

/**
 * Reasons the confirm screen answers with a gift instead of a help screen (PIT-54): the Zenlinks
 * for 3 months to someone leaving because they do not use it, when Zenpost offers it. A card at
 * the top of the same screen, never a screen of its own (no reason goes through more screens than
 * before).
 */
const RETENTION_GIFT_REASONS: readonly CancellationReason[] = ["not_using"];

/**
 * Whether the gift goes on the confirm screen. Not using it: always. Price: always too, in one offer
 * with the cheaper plan when there is one (the plan and the gift together, the owner's call on
 * 16/10) and alone when there is none (Basic monthly without the 12x, annual, Stone, a scheduled
 * change, a legacy price under the list); never while the options are still being priced
 * (`cheaperPlanOptions` null), so the screen does not change under the customer.
 */
export function offersRetentionGift(
  reason: CancellationReason | null | undefined,
  opts: { cheaperPlanOptions: number | null },
): boolean {
  if (reason == null) return false;
  if (RETENTION_GIFT_REASONS.includes(reason)) return true;
  return offersCheaperPlan(reason) && opts.cheaperPlanOptions !== null;
}

/**
 * Reasons whose gift Zenpay grants. Price is one of them: the route cannot see which plan options
 * the page priced, and Zenpost gives the gift once per account whatever the reason.
 */
export function acceptsRetentionGiftReason(reason: CancellationReason | null | undefined): boolean {
  return reason != null && (RETENTION_GIFT_REASONS.includes(reason) || offersCheaperPlan(reason));
}

/**
 * Reasons the confirm screen answers with a cheaper way to stay (PIT-452): the Basic monthly plan
 * in the gift's place. With no option to offer, price gets the gift (above).
 */
const CHEAPER_PLAN_REASONS: readonly CancellationReason[] = ["too_expensive"];

export function offersCheaperPlan(reason: CancellationReason | null | undefined): boolean {
  return reason != null && CHEAPER_PLAN_REASONS.includes(reason);
}

/**
 * Reasons that are not a wish to leave (PIT-546 f): "I want another plan" (18/10: "Troca de plano do
 * básico pro premium. Não consigo pelo app") leads to the plan picker already on the plan asked for,
 * on the same screen, with "continue cancelling" one click away.
 */
const PLAN_PICKER_REASONS: readonly CancellationReason[] = ["change_plan"];

export function leadsToPlanPicker(reason: CancellationReason | null | undefined): boolean {
  return reason != null && PLAN_PICKER_REASONS.includes(reason);
}

/** Minimum words required in the cancellation explanation for every reason. */
export const MIN_CANCELLATION_DETAIL_WORDS = 5;

export function isCancellationReason(value: unknown): value is CancellationReason {
  return (
    typeof value === "string" &&
    (CANCELLATION_REASONS as readonly string[]).includes(value)
  );
}

/** Details/comments are required for every cancellation reason. */
export function cancellationDetailsRequired(_reason: CancellationReason): boolean {
  return true;
}

export function countCancellationDetailWords(details: string | null | undefined): number {
  const trimmed = (details ?? "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

export function hasCancellationDetails(details: string | null | undefined): boolean {
  return countCancellationDetailWords(details) >= MIN_CANCELLATION_DETAIL_WORDS;
}
